import { formatMoneyShort } from '@/lib/format';
import { chartColor } from '@/lib/palette';
import { useIsDark } from '@/components/ThemeToggle';

export interface CostBar {
  key: string;
  label: string;
  color: string;
  value: number;
  count: number;
}

/**
 * Horizontal bars, one per group. Colour follows the group (the entity), never
 * its rank, and every bar carries its name and value as direct labels – so the
 * chart stays readable without relying on hue.
 */
export function GroupCostBars({
  data,
  currency,
  onSelect,
}: {
  data: CostBar[];
  currency: string;
  onSelect?: (key: string) => void;
}) {
  const isDark = useIsDark();
  const max = Math.max(...data.map((bar) => bar.value), 1);

  return (
    <ul className="flex flex-col gap-3.5">
      {data.map((bar) => {
        const color = chartColor(bar.color, isDark);
        const width = Math.max((bar.value / max) * 100, bar.value > 0 ? 1.5 : 0);
        const Row = onSelect ? 'button' : 'div';
        return (
          <li key={bar.key}>
            <Row
              {...(onSelect
                ? { type: 'button' as const, onClick: () => onSelect(bar.key) }
                : {})}
              className={`flex w-full flex-col gap-1.5 text-left ${
                onSelect ? 'cursor-pointer rounded-md transition-opacity hover:opacity-80' : ''
              }`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: color }}
                  />
                  <span className="truncate text-sm text-[var(--ink)]">{bar.label}</span>
                  <span className="num shrink-0 text-[11px] text-[var(--ink-3)]">
                    {bar.count}
                  </span>
                </span>
                <span className="num shrink-0 text-sm font-medium text-[var(--ink)]">
                  {formatMoneyShort(bar.value, currency)}
                </span>
              </div>
              {/* 10px bar, 4px rounded data-end, square at the baseline. */}
              <div className="h-2.5 w-full overflow-hidden rounded-[3px] bg-[var(--surface-3)]">
                <div
                  className="h-full rounded-r-[4px] transition-[width] duration-500"
                  style={{ width: `${width}%`, background: color }}
                  title={`${bar.label}: ${formatMoneyShort(bar.value, currency)}`}
                />
              </div>
            </Row>
          </li>
        );
      })}
    </ul>
  );
}
